import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Input, Select, Spin, Typography } from 'antd';
import RFB from '@novnc/novnc';
import { getVncTargets, VncTarget, backendPublicOrigin } from '../services/api';
import { useSidebar } from '../contexts/SidebarContext';

const { Text, Title } = Typography;

/**
 * IC-041: browser viewer for an existing legacy VNC session, bridged through this
 * backend (not IWM — see docs/backlog.md IC-041 for why). Loads the target list for
 * a person, then opens an RFB connection over the backend's WebSocket bridge.
 */
export default function VncViewerPage() {
  const { userId } = useParams<{ userId: string }>();
  const { isSidebarOpen } = useSidebar();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rfbRef = useRef<RFB | null>(null);

  const [targets, setTargets] = useState<VncTarget[] | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<'loading' | 'idle' | 'connecting' | 'connected' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  // Real target servers here run Xvnc with -rfbauth (a password is genuinely
  // required — confirmed live, "Too many security failures" without one).
  // IC does not store these passwords anywhere; the operator enters it here
  // each time. Kept in a ref (not just state) so the credentialsrequired
  // handler below always reads the latest value even if it fires after a
  // password edit mid-connection-attempt.
  const passwordRef = useRef('');
  passwordRef.current = password;

  useEffect(() => {
    if (!userId) return;
    setStatus('loading');
    getVncTargets(userId)
      .then((data) => {
        setTargets(data);
        if (data.length === 1) setSelectedId(data[0].id);
        setStatus('idle');
      })
      .catch((err) => {
        setError(err?.response?.data?.message || 'Failed to load VNC targets for this user');
        setStatus('error');
      });
  }, [userId]);

  const connect = useCallback((target: VncTarget) => {
    if (!containerRef.current) return;
    rfbRef.current?.disconnect();
    setStatus('connecting');
    setError(null);

    const httpOrigin = backendPublicOrigin() || window.location.origin;
    const wsOrigin = httpOrigin.replace(/^http/, 'ws');
    const token = localStorage.getItem('authToken') || '';
    const wsUrl = `${wsOrigin}/ws/vnc-bridge/${target.id}?token=${encodeURIComponent(token)}`;

    const rfb = new RFB(containerRef.current, wsUrl);
    rfb.scaleViewport = true;
    rfb.background = '#000';
    rfb.addEventListener('connect', () => setStatus('connected'));
    rfb.addEventListener('disconnect', (e: any) => {
      setStatus('error');
      setError(e?.detail?.clean ? 'Session ended' : 'Connection lost or could not be established');
    });
    rfb.addEventListener('securityfailure', (e: any) => {
      setStatus('error');
      setError(e?.detail?.reason || 'VNC authentication failed — check the password and try again');
    });
    rfb.addEventListener('credentialsrequired', () => {
      rfb.sendCredentials({ password: passwordRef.current });
    });
    rfbRef.current = rfb;
  }, []);

  const handleConnectClick = useCallback(() => {
    if (selectedId == null || !targets) return;
    const target = targets.find((t) => t.id === selectedId);
    if (target) connect(target);
  }, [selectedId, targets, connect]);

  useEffect(() => {
    return () => {
      rfbRef.current?.disconnect();
      rfbRef.current = null;
    };
  }, []);

  return (
    <div className={`flex-1 p-4 transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}>
      <Title level={4} className="!mb-1">VNC Session — {userId}</Title>
      <Text type="secondary">
        Bridged directly by InfraConsole (IC-041) — no IWM involved, this is a legacy-host session.
      </Text>

      {status === 'loading' && <div className="mt-4"><Spin tip="Loading VNC targets..." /></div>}

      {status !== 'loading' && targets && targets.length === 0 && (
        <Alert className="mt-4" type="warning" showIcon message="No VNC-enabled server accounts found for this user." />
      )}

      {targets && targets.length > 0 && (
        <div className="mt-4 mb-2 flex items-end gap-3 flex-wrap">
          {targets.length > 1 && (
            <div>
              <Text strong className="block mb-1">Select server</Text>
              <Select
                style={{ width: 320 }}
                value={selectedId ?? undefined}
                placeholder="Choose a server"
                onChange={(v) => setSelectedId(v)}
                options={targets.map((t) => ({ value: t.id, label: `${t.serverFqdn} (port ${t.vncPort})` }))}
              />
            </div>
          )}
          {selectedId != null && (() => {
            const selected = targets.find((t) => t.id === selectedId);
            return selected?.lastSeen ? (
              <Text type="secondary" className="text-xs">
                Last refreshed: {new Date(selected.lastSeen).toLocaleString()} — Connect always does its own live
                check regardless of this timestamp.
              </Text>
            ) : null;
          })()}
          <div>
            <Text strong className="block mb-1">VNC password</Text>
            <Input.Password
              style={{ width: 220 }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onPressEnter={handleConnectClick}
              placeholder="This account's VNC password"
              autoComplete="off"
            />
          </div>
          <Button type="primary" disabled={selectedId == null} onClick={handleConnectClick}>
            {status === 'connected' ? 'Reconnect' : 'Connect'}
          </Button>
        </div>
      )}

      {error && <Alert className="mt-4" type="error" showIcon message={error} closable onClose={() => setError(null)} />}
      {status === 'connecting' && <div className="mt-2"><Spin tip="Opening VNC bridge (SSH tunnel + connect)..." /></div>}

      <div
        ref={containerRef}
        className="mt-4 border border-gray-300 rounded"
        style={{ width: '100%', height: '70vh', background: '#111' }}
      />
    </div>
  );
}
